import { Location, MonitoringServiceProviderLocation, PrimaryContact, SecondaryContact } from '../../../../api/models'
import { hasPhoneNumberDifference, hasValueDifference } from '../../../../utils/valueComparisonUtils'

const getPrimaryContactName = (contact: PrimaryContact | undefined): string => {
  return `${contact?.name?.firstName ?? ''} ${contact?.name?.lastName ?? ''}`.trim()
}

const getSecondaryContactName = (contact: SecondaryContact | undefined): string => {
  return contact?.name ?? ''
}

const isEmptyPrimaryContact = (primaryContact?: PrimaryContact): boolean => {
  if (!primaryContact) return true

  const { name, phone } = primaryContact
  const isNameEmpty = !name?.firstName && !name?.lastName
  return isNameEmpty && !phone?.number
}

const isEmptySecondaryContact = (secondaryContact?: SecondaryContact): boolean => {
  if (!secondaryContact) return true

  const { name, phone } = secondaryContact
  return !name && !phone?.number
}

export const hasContactDifferences = (
  location: Location | undefined,
  mspLocation: MonitoringServiceProviderLocation | undefined,
  enabled: boolean
): boolean => {
  if (!enabled) return false

  const locationContacts = location?.contacts ?? { primaryContacts: [], secondaryContacts: [] }
  const mspContacts = mspLocation?.contacts ?? { primary: [], secondary: [] }

  const maxPrimaryContacts = Math.max(locationContacts.primaryContacts.length, mspContacts.primary.length)
  for (let index = 0; index < maxPrimaryContacts; index += 1) {
    const locationPrimaryContact = locationContacts.primaryContacts[index]
    const mspPrimaryContact = mspContacts.primary[index]

    // Location query returns all possible contacts, even if they are not set. They'll be objects with empty properties.
    // While the mspLocation will only return contacts that are set.
    // So we have to check if contacts are "empty" in different ways between the two datasets.
    // If both are empty, then we consider them to be matching and move on to the next contact without checking for value differences.
    // the primaryContacts in the location query have name separated into first and last,
    // while the mspLocation has it combined, so we have to use isEmptySecondaryContact for msp
    if (isEmptyPrimaryContact(locationPrimaryContact) && isEmptySecondaryContact(mspPrimaryContact)) {
      continue
    }

    if (
      hasValueDifference(getPrimaryContactName(locationPrimaryContact), mspPrimaryContact?.name, enabled) ||
      hasPhoneNumberDifference(locationPrimaryContact?.phone?.number, mspPrimaryContact?.phone?.number, enabled)
    ) {
      return true
    }
  }

  const maxSecondaryContacts = Math.max(locationContacts.secondaryContacts.length, mspContacts.secondary.length)
  for (let index = 0; index < maxSecondaryContacts; index += 1) {
    const locationSecondaryContact = locationContacts.secondaryContacts[index]
    const mspSecondaryContact = mspContacts.secondary[index]

    if (isEmptySecondaryContact(locationSecondaryContact) && isEmptySecondaryContact(mspSecondaryContact)) {
      continue
    }

    if (
      hasValueDifference(getSecondaryContactName(locationSecondaryContact), mspSecondaryContact?.name, enabled) ||
      hasPhoneNumberDifference(locationSecondaryContact?.phone?.number, mspSecondaryContact?.phone?.number, enabled)
    ) {
      return true
    }
  }

  return false
}
